import { trackAnalyticsEvent } from '../shared/analytics';

const SAMPLE_INTERVAL_MS = 10 * 60 * 1_000;
const EXCESSIVE_NETWORK_PROBE_THRESHOLD = 30;
const UPLOAD_CONCURRENCY_THRESHOLD = 5;
const RAPID_DRAIN_THRESHOLD_PERCENT_PER_HOUR = 15;
const MINIMUM_DRAIN_WINDOW_MS = 5 * 60 * 1_000;

export type EnergySampleReason = 'startup' | 'interval' | 'foreground' | 'background';

type BatteryState = 'charging' | 'discharging' | 'full' | 'unknown';

type BatterySnapshot = {
  battery_api_available: boolean;
  battery_level_percent?: number;
  battery_state: BatteryState;
  charging?: boolean;
  low_power_mode?: boolean | null;
};

export type EnergyMonitoringDependencies = {
  getBatterySnapshot(): Promise<BatterySnapshot>;
  getVisibilityState(): DocumentVisibilityState;
  addVisibilityListener(listener: () => void): () => void;
  setInterval(listener: () => void, milliseconds: number): unknown;
  clearInterval(interval: unknown): void;
  now(): number;
  trackEvent(name: string, data: Record<string, unknown>): void;
};

type WebBatteryManager = {
  charging: boolean;
  level: number;
};

type NavigatorWithBattery = Navigator & {
  getBattery?: () => Promise<WebBatteryManager>;
};

type ActivityWindow = {
  longTaskCount: number;
  longTaskDurationMs: number;
  maxConcurrentUploads: number;
  networkProbeCount: number;
  uploadAttemptCount: number;
  uploadBytes: number;
  uploadRetryCount: number;
};

async function getWebBatterySnapshot(): Promise<BatterySnapshot> {
  const getBattery = (navigator as NavigatorWithBattery).getBattery;
  if (!getBattery) {
    return {
      battery_api_available: false,
      battery_state: 'unknown',
      low_power_mode: null,
    };
  }

  try {
    const battery = await getBattery.call(navigator);
    const batteryLevelPercent = Math.round(battery.level * 100);
    const batteryState = battery.charging
      ? batteryLevelPercent >= 100
        ? 'full'
        : 'charging'
      : 'discharging';

    return {
      battery_api_available: true,
      battery_level_percent: batteryLevelPercent,
      battery_state: batteryState,
      charging: battery.charging,
      low_power_mode: null,
    };
  } catch {
    return {
      battery_api_available: false,
      battery_state: 'unknown',
      low_power_mode: null,
    };
  }
}

function addVisibilityListener(listener: () => void): () => void {
  const controller = new AbortController();
  document.addEventListener('visibilitychange', listener, { signal: controller.signal });
  return () => controller.abort();
}

const defaultDependencies: EnergyMonitoringDependencies = {
  getBatterySnapshot: getWebBatterySnapshot,
  getVisibilityState: () => document.visibilityState,
  addVisibilityListener,
  setInterval: (listener, milliseconds) => window.setInterval(listener, milliseconds),
  clearInterval: (interval) => window.clearInterval(interval as number),
  now: () => Date.now(),
  trackEvent: trackAnalyticsEvent,
};

export class EnergyMonitoringService {
  private activeUploads = 0;
  private activity: ActivityWindow = this.createActivityWindow();
  private interval?: unknown;
  private isStarted = false;
  private lastBatterySnapshot?: BatterySnapshot;
  private lastSampleAt = 0;
  private removeVisibilityListener?: () => void;
  private sampleInFlight?: Promise<void>;

  constructor(private readonly dependencies: EnergyMonitoringDependencies = defaultDependencies) {}

  async start(): Promise<void> {
    if (this.isStarted) {
      return;
    }

    this.isStarted = true;
    this.lastSampleAt = this.dependencies.now();
    this.removeVisibilityListener = this.dependencies.addVisibilityListener(() => {
      const reason = this.dependencies.getVisibilityState() === 'visible' ? 'foreground' : 'background';
      void this.sample(reason);
    });
    this.interval = this.dependencies.setInterval(() => {
      if (this.dependencies.getVisibilityState() === 'visible') {
        void this.sample('interval');
      }
    }, SAMPLE_INTERVAL_MS);

    await this.sample('startup');
  }

  stop(): void {
    if (!this.isStarted) {
      return;
    }

    this.isStarted = false;
    this.removeVisibilityListener?.();
    this.removeVisibilityListener = undefined;
    if (this.interval !== undefined) {
      this.dependencies.clearInterval(this.interval);
      this.interval = undefined;
    }
  }

  sample(reason: EnergySampleReason): Promise<void> {
    if (this.sampleInFlight) {
      return this.sampleInFlight;
    }

    this.sampleInFlight = this.collectSample(reason).finally(() => {
      this.sampleInFlight = undefined;
    });
    return this.sampleInFlight;
  }

  recordNetworkProbe(): void {
    this.activity.networkProbeCount += 1;
  }

  recordUploadStarted(fileSizeBytes?: number): void {
    this.activeUploads += 1;
    this.activity.uploadAttemptCount += 1;
    this.activity.uploadBytes += Math.max(0, fileSizeBytes ?? 0);
    this.activity.maxConcurrentUploads = Math.max(
      this.activity.maxConcurrentUploads,
      this.activeUploads,
    );
  }

  recordUploadFinished(): void {
    this.activeUploads = Math.max(0, this.activeUploads - 1);
  }

  recordUploadRetry(): void {
    this.activity.uploadRetryCount += 1;
  }

  recordLongTask(durationMs: number): void {
    this.activity.longTaskCount += 1;
    this.activity.longTaskDurationMs += Math.max(0, Math.round(durationMs));
  }

  private async collectSample(reason: EnergySampleReason): Promise<void> {
    const sampledAt = this.dependencies.now();
    const sampleWindowMs = Math.max(0, sampledAt - this.lastSampleAt);
    const activity = this.activity;
    this.activity = this.createActivityWindow();
    this.lastSampleAt = sampledAt;

    const battery = await this.dependencies.getBatterySnapshot();
    const drain = this.getBatteryDrain(battery, sampleWindowMs, reason);
    const data = {
      ...battery,
      ...drain,
      active_uploads: this.activeUploads,
      long_task_count: activity.longTaskCount,
      long_task_duration_ms: activity.longTaskDurationMs,
      max_concurrent_uploads: activity.maxConcurrentUploads,
      network_probe_count: activity.networkProbeCount,
      network_probe_count_per_10_minutes: this.getNetworkProbeRate(
        activity.networkProbeCount,
        sampleWindowMs,
      ),
      sample_reason: reason,
      sample_window_ms: sampleWindowMs,
      upload_attempt_count: activity.uploadAttemptCount,
      upload_bytes: activity.uploadBytes,
      upload_retry_count: activity.uploadRetryCount,
    } satisfies Record<string, unknown>;

    this.dependencies.trackEvent('battery_sampled', data);
    this.trackRisks(data);
    this.lastBatterySnapshot = battery;
  }

  private getBatteryDrain(
    battery: BatterySnapshot,
    sampleWindowMs: number,
    reason: EnergySampleReason,
  ): Record<string, number> {
    const previous = this.lastBatterySnapshot;
    if (
      reason === 'foreground' ||
      sampleWindowMs < MINIMUM_DRAIN_WINDOW_MS ||
      previous?.battery_level_percent === undefined ||
      battery.battery_level_percent === undefined ||
      previous.charging !== false ||
      battery.charging !== false
    ) {
      return {};
    }

    const batteryDrainPercent = Math.max(
      0,
      previous.battery_level_percent - battery.battery_level_percent,
    );
    return {
      battery_drain_percent: batteryDrainPercent,
      battery_drain_percent_per_hour: Math.round(
        (batteryDrainPercent * 60 * 60 * 1_000 * 10) / sampleWindowMs,
      ) / 10,
    };
  }

  private trackRisks(data: Record<string, unknown>): void {
    if (Number(data.network_probe_count_per_10_minutes) > EXCESSIVE_NETWORK_PROBE_THRESHOLD) {
      this.dependencies.trackEvent('energy_risk_detected', {
        risk_type: 'excessive_network_probes',
        network_probe_count: data.network_probe_count,
        network_probe_count_per_10_minutes: data.network_probe_count_per_10_minutes,
        sample_window_ms: data.sample_window_ms,
      });
    }

    if (Number(data.max_concurrent_uploads) > UPLOAD_CONCURRENCY_THRESHOLD) {
      this.dependencies.trackEvent('energy_risk_detected', {
        risk_type: 'upload_concurrency',
        max_concurrent_uploads: data.max_concurrent_uploads,
        sample_window_ms: data.sample_window_ms,
      });
    }

    if (Number(data.battery_drain_percent_per_hour) >= RAPID_DRAIN_THRESHOLD_PERCENT_PER_HOUR) {
      this.dependencies.trackEvent('energy_risk_detected', {
        risk_type: 'rapid_battery_drain',
        battery_drain_percent: data.battery_drain_percent,
        battery_drain_percent_per_hour: data.battery_drain_percent_per_hour,
        sample_window_ms: data.sample_window_ms,
      });
    }
  }

  private getNetworkProbeRate(networkProbeCount: number, sampleWindowMs: number): number {
    if (sampleWindowMs === 0) {
      return 0;
    }

    return Math.round((networkProbeCount * SAMPLE_INTERVAL_MS * 10) / sampleWindowMs) / 10;
  }

  private createActivityWindow(): ActivityWindow {
    return {
      longTaskCount: 0,
      longTaskDurationMs: 0,
      maxConcurrentUploads: this.activeUploads,
      networkProbeCount: 0,
      uploadAttemptCount: 0,
      uploadBytes: 0,
      uploadRetryCount: 0,
    };
  }
}

export const energyMonitoringService = new EnergyMonitoringService();
